import { DatePicker, Form, Modal, Row } from "antd";
import React, { useState, useEffect } from "react";
import Text from "../Text/Text";
import { CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";
import color from "../../resource/color";
import Button from "../Button/Button";
import dayjs, { Dayjs } from "dayjs";
import { useNotificationContext } from "../../context/NotificationProvider";
import { excelApprovePointPivot, excelOrderApprove } from "../../datasource/OrderPointDatasource";
import { formatFileSize } from "../../utility/formatFileSize";
import ModalError from "./ModalError";
import icon from "../../resource/icon";

interface Props {
  open: boolean;
  onClose: () => void;
  userType: string;
  onCallErr: (val) => void;
  reportType: number;
}

const ModalReportApprovePoint: React.FC<Props> = ({
  open,
  onClose,
  userType,
  onCallErr,
  reportType,
}) => {
  const [form] = Form.useForm();
  const { startUploadProcess } = useNotificationContext();
  const [progressValue, setProgressValue] = useState(0);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const companyCode = JSON.parse(localStorage.getItem("company")!)?.companyCode;

  const validateDates = () => {
    const { startDate, endDate } = form.getFieldsValue();

    if (!startDate || !endDate) {
      form.setFields([
        {
          name: !startDate ? "startDate" : "endDate",
          errors: [!startDate ? "โปรดเลือกวันที่เริ่มต้นรายงาน" : "โปรดเลือกวันที่สิ้นสุดรายงาน"],
        },
      ]);
      return false;
    }

    if (startDate.isAfter(endDate)) {
      form.setFields([
        {
          name: "endDate",
          errors: ["วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น"],
        },
      ]);
      return false;
    }

    return true;
  };

  const onFinish = async () => {
    if (!validateDates()) return;
    setLoadingExcel(true);
    onCallErr(undefined);
    try {
      const { startDate, endDate } = form.getFieldsValue();
      const requestData = {
        company: companyCode,
        userType,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      };

      const response =
        reportType == 1
          ? await excelOrderApprove(requestData)
          : await excelApprovePointPivot(requestData);

      const fileName = reportType == 1 ? "รายงานอนุมัติแต้ม_" : "รายงานอนุมัติแต้มแบบPivot_";
      if (response) {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        startUploadProcess(
          formatFileSize(blob.size),
          progressValue,
          setProgressValue,
          {
            placement: "bottomRight",
            duration: 2,
            label: "กำลังเตรียมไฟล์ของคุณ รอสักครู่!",
          },
          async () => {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `${fileName}${startDate.format("YYYY-MM-DD")}_ถึง_${endDate.format("YYYY-MM-DD")}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          },
        );
        resetForm();
      } else {
        onCallErr("err");
        resetForm();
      }
    } catch (error) {
      console.error("Error submitting report:", error);
    } finally {
      setLoadingExcel(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal open={open} closable={false} centered footer={null}>
      <Text fontFamily='IBM Plex Sans Thai' fontSize={24} fontWeight={700}>
        {reportType == 1 ? "ดาวน์โหลดรายงานอนุมัติแต้ม" : "ดาวน์โหลดรายงานอนุมัติแต้มแบบ Pivot"}
      </Text>
      <Text
        fontFamily='Sarabun'
        fontSize={12}
        fontWeight={400}
        color='Text3'
        style={{ display: "flex" }}
      >
        <InfoCircleOutlined
          style={{ fontSize: "16px", color: color.primary, marginRight: "4px" }}
        />
        สามารถดาวน์โหลดรายการย้อนหลังสูงสุดได้ 2 ปี
      </Text>
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Row gutter={8}>
          {["startDate", "endDate"].map((field, idx) => (
            <Form.Item
              key={field}
              label={field === "startDate" ? "วันที่เริ่มต้น" : "วันที่สิ้นสุด"}
              name={field}
              style={{ flex: 1, margin: 6 }}
              rules={[
                {
                  required: true,
                  message: `โปรดเลือก${field === "startDate" ? "วันที่เริ่มต้น" : "วันที่สิ้นสุด"}รายงาน`,
                },
              ]}
            >
              <DatePicker
                suffixIcon={<CalendarOutlined />}
                style={{ width: "100%", height: "40px" }}
                format='DD/MM/YYYY'
                disabledDate={(current) =>
                  current &&
                  (current.isBefore(dayjs().subtract(2, "year"), "day") ||
                    current.isAfter(dayjs(), "day"))
                }
              />
            </Form.Item>
          ))}
        </Row>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid #E5E5E5",
          }}
        >
          <Button
            style={{ width: "100px", marginRight: 12 }}
            height={40}
            title='ยกเลิก'
            typeButton='primary-light'
            onClick={resetForm}
          />
          <div style={{ marginRight: 4 }} />
          <Button
            style={{ width: "max-content" }}
            height={40}
            title='ดาวน์โหลด'
            htmlType='submit'
            loading={loadingExcel}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default ModalReportApprovePoint;
